import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbBell } from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { useRunningChats } from "./useRunningChats";

export const NotificationBell: Component = () => {
  const wire = useWire();
  const { t } = useI18n();
  const notifications = wire.notifications;

  const countLabel = () => (notifications.unreadCount() > 9 ? "9+" : String(notifications.unreadCount()));

  const running = useRunningChats();
  const runningCount = () => running().length;

  const baseAriaLabel = () => t("notifications.bell.aria_label", { count: notifications.unreadCount() });
  const ariaLabel = () =>
    runningCount() > 0
      ? `${baseAriaLabel()}, ${t("notifications.bell.running", { count: runningCount() })}`
      : baseAriaLabel();
  const tooltipContent = () =>
    runningCount() > 0 ? t("notifications.bell.running", { count: runningCount() }) : baseAriaLabel();

  return (
    <StTooltip content={<span>{tooltipContent()}</span>} placement="bottom">
      <StTooltipTrigger as="div" class="flex">
        <button
          type="button"
          onClick={() => {
            if (!wire.ui.layout.notificationsOpen())
              trackFeature({ feature: "notifications", action: "open", via: "button" });
            wire.ui.layout.setNotificationsOpen(!wire.ui.layout.notificationsOpen());
          }}
          aria-label={ariaLabel()}
          class="flex cursor-pointer items-center justify-center rounded-md p-2 text-secondary-text transition-colors hover:bg-canvas hover:text-primary-text"
        >
          {/* Block-level so the span doesn't sit on the button's text baseline —
              inline placement adds descender space that nudges the icon upward
              relative to the sibling buttons' bare icons. */}
          <span class="relative flex">
            <TbBell class="size-5" aria-hidden="true" />
            <Show when={runningCount() > 0}>
              <span
                aria-hidden="true"
                class="absolute -bottom-1 -left-1 size-1.5 animate-pulse rounded-full bg-accent motion-reduce:animate-none"
              />
            </Show>
            <Show when={notifications.unreadCount() > 0}>
              <span class="absolute -top-1.5 -right-2 flex min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[0.6rem] leading-4 font-semibold text-on-brand">
                {countLabel()}
              </span>
            </Show>
          </span>
        </button>
      </StTooltipTrigger>
    </StTooltip>
  );
};
